import api from './api'
import i18n from '../i18n'

export type PaymentProvider = 'PAYU' | 'STRIPE' | 'PAYPAL' | 'PRZELEWY24' | 'KLARNA'

export interface PaymentMethod {
  uuid: string
  club_uuid: string
  provider: PaymentProvider
  configuration: Record<string, any>
  commission: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface CreatePaymentMethodDto {
  provider: PaymentProvider
  configuration: Record<string, any>
  commission?: number
}

export interface UpdatePaymentMethodDto {
  configuration: Record<string, any>
  commission?: number
}

/**
 * Payment Method Service
 * Handles all payment method-related API calls
 */
class PaymentMethodService {
  /**
   * Get active payment method for a club
   * GET v1/{club}/payment-method
   */
  async getPaymentMethod(clubUuid: string): Promise<{ payment_method: PaymentMethod }> {
    const response = await api.get(`v1/${clubUuid}/payment-method`)
    return response.data
  }

  /**
   * Create new payment method
   * POST v1/{club}/payment-method
   */
  async createPaymentMethod(clubUuid: string, data: CreatePaymentMethodDto): Promise<{ message: string; payment_method: PaymentMethod }> {
    const response = await api.post(`v1/${clubUuid}/payment-method`, data)
    return response.data
  }

  /**
   * Update payment method
   * PUT v1/{club}/payment-method
   */
  async updatePaymentMethod(clubUuid: string, data: UpdatePaymentMethodDto): Promise<{ message: string; payment_method: PaymentMethod }> {
    const response = await api.put(`v1/${clubUuid}/payment-method`, data)
    return response.data
  }

  /**
   * Delete payment method
   * DELETE v1/{club}/payment-method
   */
  async deletePaymentMethod(clubUuid: string): Promise<{ message: string }> {
    const response = await api.delete(`v1/${clubUuid}/payment-method`)
    return response.data
  }

  /**
   * Get Stripe webhook URL
   * GET v1/stripe/webhook-url
   */
  async getStripeWebhookUrl(): Promise<string> {
    const response = await api.get('v1/stripe/webhook-url')
    return response.data.webhook_url
  }

  /**
   * Get provider display name
   */
  getProviderName(provider: PaymentProvider): string {
    const names: Record<PaymentProvider, string> = {
      PAYU: 'PayU',
      STRIPE: 'Stripe',
      PAYPAL: 'PayPal',
      PRZELEWY24: 'Przelewy24',
      KLARNA: 'Klarna'
    }
    return names[provider] || provider
  }

  /**
   * Get environment display name
   */
  getEnvironmentName(environment: string): string {
    const names: Record<string, string> = {
      sandbox: 'Sandbox (testowe)',
      production: 'Production (produkcyjne)',
      test: 'Test (testowe)',
      live: 'Live (produkcyjne)',
      playground: 'Playground (testowe)'
    }
    return names[environment] || environment
  }

  /**
   * Validate provider configuration
   */
  validateConfiguration(provider: PaymentProvider, config: Record<string, any>): string | null {
    const t = i18n.global.t as (key: string, params?: Record<string, string>) => string

    switch (provider) {
      case 'PAYU':
        if (!config.merchant_pos_id) return t('paymentMethodValidation.fieldRequired', { field: 'Merchant POS ID' })
        if (!config.client_id) return t('paymentMethodValidation.fieldRequired', { field: 'Client ID' })
        if (!config.client_secret) return t('paymentMethodValidation.fieldRequired', { field: 'Client Secret' })
        if (!config.signature_key) return t('paymentMethodValidation.fieldRequired', { field: 'Signature Key' })
        if (!config.environment) return t('paymentMethodValidation.environmentRequired')
        break

      case 'STRIPE':
        if (!config.publishable_key) return t('paymentMethodValidation.fieldRequired', { field: 'Publishable Key' })
        if (!config.secret_key) return t('paymentMethodValidation.fieldRequired', { field: 'Secret Key' })
        if (!config.webhook_secret) return t('paymentMethodValidation.fieldRequired', { field: 'Webhook Secret' })
        if (!config.environment) return t('paymentMethodValidation.environmentRequired')
        break

      case 'PAYPAL':
        if (!config.client_id) return t('paymentMethodValidation.fieldRequired', { field: 'Client ID' })
        if (!config.client_secret) return t('paymentMethodValidation.fieldRequired', { field: 'Client Secret' })
        if (!config.environment) return t('paymentMethodValidation.environmentRequired')
        break

      case 'PRZELEWY24':
        if (!config.merchant_id) return t('paymentMethodValidation.fieldRequired', { field: 'Merchant ID' })
        if (!config.pos_id) return t('paymentMethodValidation.fieldRequired', { field: 'POS ID' })
        if (!config.crc_key) return t('paymentMethodValidation.fieldRequired', { field: 'CRC Key' })
        if (!config.api_key) return t('paymentMethodValidation.fieldRequired', { field: 'API Key' })
        if (!config.environment) return t('paymentMethodValidation.environmentRequired')
        break

      case 'KLARNA':
        if (!config.username) return t('paymentMethodValidation.fieldRequired', { field: 'Username' })
        if (!config.password) return t('paymentMethodValidation.fieldRequired', { field: 'Password' })
        if (!config.region) return t('paymentMethodValidation.fieldRequired', { field: 'Region' })
        if (!config.environment) return t('paymentMethodValidation.environmentRequired')
        break

      default:
        return t('paymentMethodValidation.unknownProvider')
    }

    return null
  }
}

export default new PaymentMethodService()
